/**
 * SimilarTracesDialog component
 *
 * Displays similar traces found by the search action using expandable
 * compact rows with severity indicators, anomaly scores, and a
 * "Link to Issue" button for unlinked traces.
 */

import { useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link2, Loader2, RefreshCw, ChevronRight } from "lucide-react";
import { useLinkTrace } from "@/hooks/useLinkTrace";
import { SEVERITY_DOT, SEVERITY_BADGE } from "@/constants/severity";

export interface SimilarTrace {
  trace_id: number;
  source: string;
  source_id: string;
  event_type: string;
  timestamp: string | null;
  severity: string;
  anomaly_score: number;
  already_linked: boolean;
  content_preview?: string | null;
  correlation_id?: string | null;
  tags?: string[] | null;
  span_name?: string | null;
  model_name?: string | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
}

interface SimilarTracesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: number;
  traces: SimilarTrace[];
  totalFound: number;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export function SimilarTracesDialog({
  open,
  onOpenChange,
  issueId,
  traces,
  totalFound,
  onRegenerate,
  isRegenerating,
}: SimilarTracesDialogProps) {
  const linkTrace = useLinkTrace();
  const [expandedTraces, setExpandedTraces] = useState<Set<number>>(new Set());

  const handleLink = useCallback(
    (traceId: number) => {
      linkTrace.mutate({ issueId, traceId });
    },
    [issueId, linkTrace],
  );

  const toggleTrace = useCallback((traceId: number) => {
    setExpandedTraces((prev) => {
      const next = new Set(prev);
      if (next.has(traceId)) {
        next.delete(traceId);
      } else {
        next.add(traceId);
      }
      return next;
    });
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Similar Traces Found</DialogTitle>
          <DialogDescription>
            {totalFound} trace{totalFound !== 1 ? "s" : ""} found with similar
            patterns to this security issue.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 min-h-0 p-1 py-2">
          {traces.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No similar traces found.
            </p>
          ) : (
            <div className="rounded-lg border divide-y divide-border">
              {traces.map((trace) => {
                const isExpanded = expandedTraces.has(trace.trace_id);
                const hasDetails =
                  trace.content_preview != null ||
                  trace.source != null ||
                  trace.model_name != null ||
                  trace.timestamp != null ||
                  trace.prompt_tokens != null ||
                  trace.completion_tokens != null ||
                  (trace.tags != null && trace.tags.length > 0);

                return (
                  <div key={trace.trace_id}>
                    {/* Summary row */}
                    <div
                      className={`px-3.5 py-2.5 flex items-center gap-3 text-sm ${
                        hasDetails
                          ? "cursor-pointer hover:bg-muted/30 transition-colors"
                          : ""
                      }`}
                      onClick={() => hasDetails && toggleTrace(trace.trace_id)}
                    >
                      {hasDetails && (
                        <ChevronRight
                          className={`h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform ${
                            isExpanded ? "rotate-90" : ""
                          }`}
                        />
                      )}
                      <span
                        className={`h-2 w-2 rounded-full flex-shrink-0 ${
                          SEVERITY_DOT[trace.severity] ?? "bg-gray-400"
                        }`}
                      />
                      <span className="font-mono text-xs text-muted-foreground flex-shrink-0">
                        #{trace.trace_id}
                      </span>
                      <span className="text-xs truncate">
                        {trace.event_type}
                      </span>
                      <Badge
                        className={`text-[10px] font-medium uppercase px-1.5 py-0 border ml-auto flex-shrink-0 ${
                          SEVERITY_BADGE[trace.severity] ?? SEVERITY_BADGE.info
                        }`}
                      >
                        {trace.severity}
                      </Badge>
                      {trace.anomaly_score > 0 && (
                        <span
                          className={`text-xs tabular-nums flex-shrink-0 ${
                            trace.anomaly_score >= 0.75
                              ? "text-red-600 dark:text-red-400"
                              : trace.anomaly_score >= 0.5
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-muted-foreground"
                          }`}
                        >
                          {trace.anomaly_score.toFixed(2)}
                        </span>
                      )}
                      {trace.already_linked ? (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 flex-shrink-0"
                        >
                          Linked
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs gap-1 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLink(trace.trace_id);
                          }}
                          disabled={linkTrace.isPending}
                        >
                          {linkTrace.isPending &&
                          linkTrace.variables?.traceId === trace.trace_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Link2 className="h-3 w-3" />
                          )}
                          Link
                        </Button>
                      )}
                    </div>

                    {/* Expanded detail panel */}
                    {isExpanded && hasDetails && (
                      <div className="px-3.5 pb-3 pt-1 bg-muted/20 border-t border-dashed">
                        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 ml-6">
                          {trace.source && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">
                                Source
                              </dt>
                              <dd className="text-xs">{trace.source}</dd>
                            </div>
                          )}
                          {trace.model_name && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">
                                Model
                              </dt>
                              <dd className="text-xs font-mono">
                                {trace.model_name}
                              </dd>
                            </div>
                          )}
                          {trace.timestamp && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">
                                Timestamp
                              </dt>
                              <dd className="text-xs">
                                {new Date(trace.timestamp).toLocaleString()}
                              </dd>
                            </div>
                          )}
                          {(trace.prompt_tokens != null || trace.completion_tokens != null) && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">
                                Tokens
                              </dt>
                              <dd className="text-xs tabular-nums">
                                {trace.prompt_tokens ?? 0} in /{" "}
                                {trace.completion_tokens ?? 0} out
                              </dd>
                            </div>
                          )}
                          {trace.span_name && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">
                                Span
                              </dt>
                              <dd className="text-xs font-mono">
                                {trace.span_name}
                              </dd>
                            </div>
                          )}
                          {trace.anomaly_score > 0 && (
                            <div>
                              <dt className="text-[11px] text-muted-foreground">
                                Anomaly
                              </dt>
                              <dd className="flex items-center gap-2">
                                <div className="h-1 w-16 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      trace.anomaly_score >= 0.75
                                        ? "bg-red-500"
                                        : trace.anomaly_score >= 0.5
                                          ? "bg-orange-500"
                                          : "bg-blue-400"
                                    }`}
                                    style={{
                                      width: `${Math.min(trace.anomaly_score * 100, 100)}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-xs tabular-nums">
                                  {trace.anomaly_score.toFixed(2)}
                                </span>
                              </dd>
                            </div>
                          )}
                        </dl>
                        {trace.content_preview && (
                          <div className="mt-2 ml-6 rounded bg-muted/50 p-2 text-xs text-muted-foreground font-mono whitespace-pre-wrap break-words line-clamp-4">
                            {trace.content_preview}
                          </div>
                        )}
                        {trace.tags && trace.tags.length > 0 && (
                          <div className="mt-2 ml-6 flex flex-wrap gap-1">
                            {trace.tags.map((tag) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onRegenerate && (
            <Button
              variant="outline"
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="gap-1.5"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isRegenerating ? "animate-spin" : ""}`}
              />
              {isRegenerating ? "Searching..." : "Re-search"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
